import { Table, Button, Tag, message, Select, Input, Space, DatePicker, Modal, Timeline, Spin, Typography, Drawer, Badge, Tooltip } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useUniUniShipments } from '../hooks/useUniUniShipments';
import { useStores } from '../hooks/useStores';
import { useElementHeight } from '../hooks/useElementHeight';
import { useDebounced } from '../hooks/useDebounced';
import { printUniUniLabel, printUniUniLabelsBulk, downloadPackingSlip, fetchUniUniTracking, voidShipment, type VoidShipmentResult } from '../api/uniuniShipments.api';
import BatchStatusButton from '../components/BatchStatusButton';
import PdfPreviewModal from '../components/PdfPreviewModal';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { fetchPickupAddresses } from '../api/pickup_address.api';
import { listUsersDirectoryAPI } from '../api/auth.api';

const OP_OPTIONS = [
  { label: 'Contains', value: 'contains' },
  { label: 'Equals', value: 'equals' },
  { label: 'Starts With', value: 'startsWith' },
  { label: 'Ends With', value: 'endsWith' },
];

const STATUS_META: Record<string, { color: string; label: string }> = {
  label_created:      { color: 'blue',   label: 'Label Created' },
  submitted:          { color: 'blue',   label: 'Label Created' },
  in_transit:         { color: 'blue',   label: 'In Transit' },
  out_for_delivery:   { color: 'blue',   label: 'Out for Delivery' },
  delivery_attempted: { color: 'orange', label: 'Delivery Attempted' },
  rescheduled:        { color: 'orange', label: 'Rescheduled' },
  address_issue:      { color: 'red',    label: 'Address Issue' },
  exception:          { color: 'red',    label: 'Exception' },
  rts:                { color: 'red',    label: 'RTS' },
  lost:               { color: 'red',    label: 'Lost' },
  voided:             { color: 'red',    label: 'Voided' },
  cancelled:          { color: 'red',    label: 'Voided' },
  delivered:          { color: 'green',  label: 'Delivered' },
};

function statusMeta(status: string | null): { color: string; label: string } {
  if (!status) return { color: 'default', label: 'UNKNOWN' };
  return STATUS_META[status.toLowerCase()] ?? { color: 'default', label: status };
}

async function handlePrintLabel(trackingNumber: string) {
  const hide = message.loading('Fetching label...', 0);
  try {
    const resp = await printUniUniLabel(trackingNumber);
    hide();
    const blob = resp.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `label-${trackingNumber}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err: any) {
    hide();
    const errBlob = err?.response?.data as Blob | undefined;
    if (errBlob) {
      const text = await errBlob.text();
      try { message.error(JSON.parse(text)?.error ?? text); } catch { message.error(text); }
    } else {
      message.error(err?.message ?? 'Label fetch failed');
    }
  }
}

async function handleDownloadPackingSlip(orderId: number, orderNumber: string) {
  const hide = message.loading('Generating packing slip...', 0);
  try {
    const resp = await downloadPackingSlip(orderId);
    hide();
    const url = URL.createObjectURL(resp.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packing-slip-${orderNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err: any) {
    hide();
    message.error(err?.message ?? 'Packing slip failed');
  }
}

async function handlePrintLabelsSelected(shipments: { packageId: string; orderId: number }[]) {
  const messageKey = 'bulk-download';
  message.loading({
    content: `Fetching labels + packing slips for ${shipments.length} shipment(s)...`,
    key: messageKey,
    duration: 0,
  });
  try {
    const resp = await printUniUniLabelsBulk(shipments);
    message.destroy(messageKey);
    const blob = resp.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `labels-slips-${Date.now()}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    message.success({ content: `Downloaded ${shipments.length} labels + slips`, duration: 3 });
  } catch (err: any) {
    message.destroy(messageKey);
    const errBlob = err?.response?.data as Blob | undefined;
    if (errBlob) {
      const text = await errBlob.text();
      try {
        const parsed = JSON.parse(text);
        message.error(parsed?.error ?? text);
      } catch {
        message.error(text);
      }
    } else {
      message.error(err?.message ?? 'Bulk label fetch failed');
    }
  }
}

const BASE_FILTERS = { page: 1, pageSize: 20 };

const EMPTY_LOCAL = {
  orderNumberOp: 'contains', orderNumber: '',
  trackingOp: 'contains', tracking: '',
  nameOp: 'contains', name: '',
  emailOp: 'contains', email: '',
  cityOp: 'contains', city: '',
  postalCodeOp: 'contains', postalCode: '',
  batchId: '',
  storeIds: [] as number[],
  // Ship From (SS V1 warehouseId) + Created By (local user id).
  // Added 2026-09-16 so teams can isolate their batches and avoid
  // downloading labels for shipments another team already handled.
  warehouseIds: [] as string[],
  createdBy: [] as number[],
  from: undefined as string | undefined,
  to: undefined as string | undefined,
};

function trackingColor(code: string) {
  if (code === 'DELIVERED') return 'green';
  if (code?.includes('RETURN') || code?.includes('FAIL') || code === 'STORAGE_30_DAYS_FROM_OFFICE') return 'red';
  return 'blue';
}

export default function UniUniShipmentsReport() {
  const navigate = useNavigate();
  const [toolbarRef, toolbarHeight] = useElementHeight<HTMLDivElement>();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, 300);
  const [filters, setFilters] = useState<any>(BASE_FILTERS);
  useEffect(() => {
    setFilters((prev: any) => {
      const next = { ...prev, page: 1 };
      if (debouncedSearch.trim()) next.search = debouncedSearch.trim();
      else delete next.search;
      return next;
    });
  }, [debouncedSearch]);
  const [local, setLocal] = useState<any>(EMPTY_LOCAL);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    title: string;
    filename: string;
    fetchBlob: () => Promise<Blob>;
  }>({ open: false, title: '', filename: '', fetchBlob: async () => new Blob() });

  const openLabelPreview = (trackingNumber: string) => {
    setPreviewModal({
      open: true,
      title: `Label — ${trackingNumber}`,
      filename: `label-${trackingNumber}.pdf`,
      fetchBlob: async () => {
        const resp = await printUniUniLabel(trackingNumber);
        return resp.data as Blob;
      },
    });
  };

  const openSlipPreview = (orderId: number, orderNumber: string) => {
    setPreviewModal({
      open: true,
      title: `Packing Slip — ${orderNumber}`,
      filename: `packing-slip-${orderNumber}.pdf`,
      fetchBlob: async () => {
        const resp = await downloadPackingSlip(orderId);
        return resp.data as Blob;
      },
    });
  };

  const openLabelPlusSlipPreview = (trackingNumber: string, orderId: number, orderNumber: string) => {
    setPreviewModal({
      open: true,
      title: `Label + Slip — ${orderNumber}`,
      filename: `label-slip-${orderNumber}.pdf`,
      fetchBlob: async () => {
        const resp = await printUniUniLabelsBulk([{ packageId: trackingNumber, orderId }]);
        return resp.data as Blob;
      },
    });
  };
  const [trackingModal, setTrackingModal] = useState<{ open: boolean; trackingNumber: string; loading: boolean; events: any[] }>({
    open: false, trackingNumber: '', loading: false, events: [],
  });
  const queryClient = useQueryClient();
  const stores = useStores();
  const storeNameById = new Map<number, string>(
    (stores ?? []).map((s: any) => [Number(s.storeId), s.storeName as string]),
  );
  const storeOptions = (stores ?? []).map((s: any) => ({
    label: `${s.storeName}`,
    value: Number(s.storeId),
  }));
  // Pickup + user directories for the Ship From + Created By filters
  // (added 2026-09-16). Cached 5 min — both change rarely.
  const { data: pickups = [] } = useQuery<any[]>({
    queryKey: ['pickup-addresses'],
    queryFn: fetchPickupAddresses,
    staleTime: 5 * 60_000,
  });
  const shipFromOptions = pickups
    .filter((p: any) => p.ss_warehouse_id != null && p.ss_warehouse_id !== '')
    .map((p: any) => ({
      label: `${p.name} (SS #${p.ss_warehouse_id})`,
      value: String(p.ss_warehouse_id),
    }));
  const shipFromNameById = new Map<string, string>(
    pickups
      .filter((p: any) => p.ss_warehouse_id != null && p.ss_warehouse_id !== '')
      .map((p: any) => [String(p.ss_warehouse_id), p.name as string]),
  );
  const { data: usersData = [] } = useQuery<any[]>({
    queryKey: ['users-directory'],
    queryFn: listUsersDirectoryAPI,
    staleTime: 5 * 60_000,
  });
  const userOptions = usersData.map((u: any) => ({
    label: `${(`${u.firstname ?? ''} ${u.lastname ?? ''}`).trim() || u.email} (${u.email})`,
    value: Number(u.id),
  }));
  const userNameById = new Map<number, string>(
    usersData.map((u: any) => [
      Number(u.id),
      `${(`${u.firstname ?? ''} ${u.lastname ?? ''}`).trim() || u.email}`,
    ]),
  );
  const [voidModal, setVoidModal] = useState<{
    open: boolean;
    orderId: number | string | null;
    orderNumber: string | null;
    trackingNumber: string | null;
    reason: string;
    submitting: boolean;
    result: VoidShipmentResult | null;
  }>({ open: false, orderId: null, orderNumber: null, trackingNumber: null, reason: '', submitting: false, result: null });

  const openVoid = (record: any) => setVoidModal({
    open: true,
    orderId: record.orderId,
    orderNumber: record.orderNumber ?? null,
    trackingNumber: record.tracking_number ?? null,
    reason: '',
    submitting: false,
    result: null,
  });

  const submitVoid = async () => {
    if (voidModal.orderId == null) return;
    setVoidModal((s) => ({ ...s, submitting: true }));
    try {
      const result = await voidShipment(voidModal.orderId, voidModal.reason || undefined);
      setVoidModal((s) => ({ ...s, submitting: false, result }));
      queryClient.invalidateQueries({ queryKey: ['uniuniShipments'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    } catch (err: any) {
      message.error(err?.response?.data?.error?.message ?? err?.message ?? 'Void failed');
      setVoidModal((s) => ({ ...s, submitting: false }));
    }
  };

  async function openTracking(trackingNumber: string) {
    setTrackingModal({ open: true, trackingNumber, loading: true, events: [] });
    try {
      const result = await fetchUniUniTracking(trackingNumber);
      // Backend shape: { region, events: { status, data: { tracking_events: [...] } } }
      const events =
        result?.events?.data?.tracking_events
        ?? result?.data?.tracking_events   // some legacy shapes
        ?? result?.data
        ?? [];
      setTrackingModal(prev => ({ ...prev, loading: false, events }));
    } catch (err: any) {
      setTrackingModal(prev => ({ ...prev, loading: false }));
      message.error(err?.message ?? 'Failed to fetch tracking');
    }
  }
  const { data, isLoading, isFetching } = useUniUniShipments(filters);

  const selectedShipments = data?.shipments?.filter((s: any) => selectedRowKeys.includes(s.id)) ?? [];
  const selectedShipmentObjects = selectedShipments
    .filter((s: any) => s.tracking_number)
    .map((s: any) => ({ packageId: s.tracking_number, orderId: s.orderId }));

  function applyFilters() {
    // Field names must match backend listQuerySchema in uniuni.controller.ts.
    // Backend now joins shipstation_orders so name/email/city/postal work.
    const next: any = { ...BASE_FILTERS };
    if (local.orderNumber) next.orderNumber    = local.orderNumber;
    if (local.tracking)    next.trackingNumber = local.tracking;
    if (local.status)      next.status         = local.status;
    if (local.from)        next.dateFrom       = local.from;
    if (local.to)          next.dateTo         = local.to;
    if (local.name)        next.name           = local.name;
    if (local.email)       next.email          = local.email;
    if (local.city)        next.city           = local.city;
    if (local.postalCode)  next.postalCode     = local.postalCode;
    if (local.batchId)     next.batchId        = local.batchId.trim();
    if (Array.isArray(local.storeIds) && local.storeIds.length > 0) {
      next.storeIds = local.storeIds.join(',');
    }
    if (Array.isArray(local.warehouseIds) && local.warehouseIds.length > 0) {
      next.warehouseIds = local.warehouseIds.join(',');
    }
    if (Array.isArray(local.createdBy) && local.createdBy.length > 0) {
      next.createdBy = local.createdBy.join(',');
    }
    setFilters(next);
    setSelectedRowKeys([]);
  }

  function clearFilters() {
    setLocal(EMPTY_LOCAL);
    setSearchInput('');
    setFilters(BASE_FILTERS);
    setSelectedRowKeys([]);
  }

  // Chips derived from the *applied* filters (not the draft `local`). Each
  // chip removes its own filter both from `filters` (drives the query) and
  // `local` (keeps the drawer in sync).
  const chipDefs: Array<{ key: string; label: string; onRemove: () => void }> = [];
  const stripFilter = (k: string, localReset?: Record<string, any>) => {
    setFilters((f: any) => {
      const { [k]: _, ...rest } = f;
      return { ...rest, page: 1 };
    });
    if (localReset) setLocal((l: any) => ({ ...l, ...localReset }));
  };
  if (filters.search) chipDefs.push({ key: 'search', label: `Search: ${filters.search}`, onRemove: () => { setSearchInput(''); stripFilter('search'); } });
  if (filters.orderNumber) chipDefs.push({ key: 'orderNumber', label: `Order: ${filters.orderNumber}`, onRemove: () => stripFilter('orderNumber', { orderNumber: '' }) });
  if (filters.trackingNumber) chipDefs.push({ key: 'trackingNumber', label: `Tracking: ${filters.trackingNumber}`, onRemove: () => stripFilter('trackingNumber', { tracking: '' }) });
  if (filters.status) chipDefs.push({ key: 'status', label: `Status: ${statusMeta(filters.status).label}`, onRemove: () => stripFilter('status', { status: undefined }) });
  if (filters.batchId) chipDefs.push({ key: 'batchId', label: `Batch: ${filters.batchId.slice(0, 8)}…`, onRemove: () => stripFilter('batchId', { batchId: '' }) });
  if (filters.storeIds) {
    const ids = String(filters.storeIds).split(',').map((s) => Number(s.trim())).filter(Boolean);
    const names = ids.map((id) => storeNameById.get(id) ?? `Store ${id}`).join(', ');
    chipDefs.push({ key: 'storeIds', label: `Store: ${names}`, onRemove: () => stripFilter('storeIds', { storeIds: [] }) });
  }
  if (filters.warehouseIds) {
    const ids = String(filters.warehouseIds).split(',').map((s) => s.trim()).filter(Boolean);
    const label = ids.length > 3
      ? `${ids.length} warehouses`
      : ids.map((id) => shipFromNameById.get(id) ?? `#${id}`).join(', ');
    chipDefs.push({ key: 'warehouseIds', label: `Ship From: ${label}`, onRemove: () => stripFilter('warehouseIds', { warehouseIds: [] }) });
  }
  if (filters.createdBy) {
    const ids = String(filters.createdBy).split(',').map((s) => Number(s.trim())).filter(Boolean);
    const names = ids.map((id) => userNameById.get(id) ?? `#${id}`).join(', ');
    chipDefs.push({ key: 'createdBy', label: `Created By: ${names}`, onRemove: () => stripFilter('createdBy', { createdBy: [] }) });
  }
  if (filters.dateFrom || filters.dateTo) {
    chipDefs.push({
      key: 'date',
      label: `Created: ${filters.dateFrom ?? '…'} → ${filters.dateTo ?? '…'}`,
      onRemove: () => {
        setFilters((f: any) => { const { dateFrom, dateTo, ...rest } = f; return { ...rest, page: 1 }; });
        setLocal((l: any) => ({ ...l, from: undefined, to: undefined }));
      },
    });
  }
  if (filters.name)       chipDefs.push({ key: 'name',       label: `Name: ${filters.name}`,             onRemove: () => stripFilter('name', { name: '' }) });
  if (filters.email)      chipDefs.push({ key: 'email',      label: `Email: ${filters.email}`,           onRemove: () => stripFilter('email', { email: '' }) });
  if (filters.city)       chipDefs.push({ key: 'city',       label: `City: ${filters.city}`,             onRemove: () => stripFilter('city', { city: '' }) });
  if (filters.postalCode) chipDefs.push({ key: 'postalCode', label: `Postal: ${filters.postalCode}`,     onRemove: () => stripFilter('postalCode', { postalCode: '' }) });

  return (
    <div style={{ width: '100%' }}>
      {/* Sticky toolbar: title + actions + active-filter chips stay pinned. */}
      <div ref={toolbarRef} style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: '#fff',
        padding: '8px 16px',
        boxShadow: '0 2px 4px -2px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Space>
            <h2 style={{ margin: 0 }}>UniUni Shipments</h2>
            <Input.Search
              placeholder="Search order #, tracking, batch, customer name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => setSearchInput(v)}
              allowClear
              style={{ width: 360 }}
            />
            <Badge count={chipDefs.length} size="small" offset={[-4, 4]}>
              <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
                Filters
              </Button>
            </Badge>
            <Button
              type="primary"
              disabled={selectedShipmentObjects.length === 0}
              onClick={() => handlePrintLabelsSelected(selectedShipmentObjects)}
            >
              Download Labels + Slips {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
            </Button>
            <BatchStatusButton />
          </Space>
          <span><strong>Total Records:</strong> {data?.total ?? 0}</span>
        </div>
        {chipDefs.length > 0 && (
          <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#666', fontSize: 12 }}>Active filters:</span>
            {chipDefs.map((c) => (
              <Tag key={c.key} closable onClose={c.onRemove}>{c.label}</Tag>
            ))}
            <a onClick={clearFilters} style={{ fontSize: 12 }}>Clear all</a>
          </div>
        )}
      </div>

      <div>
        <Table
          sticky={{ offsetHeader: toolbarHeight }}
          loading={{ spinning: isLoading || isFetching, tip: 'Loading...' }}
          rowKey="id"
          dataSource={data?.shipments ?? []}
          scroll={{ x: true }}
          pagination={{
            current: filters.page,
            pageSize: filters.pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
          }}
          onChange={(pagination: any) => {
            setFilters((f: any) => ({
              ...f,
              page: pagination.current,
              pageSize: pagination.pageSize,
            }));
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            preserveSelectedRowKeys: false,
          }}
          columns={[
            {
              title: 'Order Number',
              dataIndex: 'orderNumber',
              render: (val: string, record: any) => (
                <a onClick={() => navigate(`/order/${record.orderId}`)} style={{ cursor: 'pointer' }}>
                  {val}
                </a>
              ),
            },
            {
              title: 'Store',
              dataIndex: 'storeId',
              width: 150,
              render: (id: number | null) =>
                id != null ? (storeNameById.get(Number(id)) ?? `Store ${id}`) : '-',
            },
            {
              title: 'Batch',
              dataIndex: 'batch_id',
              width: 130,
              render: (b: string | null) =>
                b ? (
                  <a
                    onClick={() => {
                      setLocal((s: any) => ({ ...s, batchId: b }));
                      setFilters({ ...BASE_FILTERS, batchId: b });
                      setSelectedRowKeys([]);
                    }}
                    title="Click to filter by this batch"
                  >
                    {b.slice(0, 8)}…
                  </a>
                ) : '-',
            },
            // "UniUni Order ID" and "Order SN" columns removed
            // (2026-09-09) — UniUni doesn't populate these on their
            // /orders/createbusinessorder response, so they were always
            // empty. Data still stored in uniuni_shipments.uniuni_order_id
            // and .order_sn columns in case UniUni starts returning them.
            {
              title: 'UniUni Tracking',
              dataIndex: 'tracking_number',
              render: (tn: string) =>
                tn ? (
                  <a onClick={() => openTracking(tn)} style={{ cursor: 'pointer' }}>
                    {tn}
                  </a>
                ) : '-',
            },
            {
              title: 'Status',
              dataIndex: 'uni_order_status',
              render: (s: string) => {
                const meta = statusMeta(s);
                return <Tag color={meta.color}>{meta.label}</Tag>;
              },
            },
            // SS Shipment ID / SS Tracking hidden - SS's public API has no
            // path to populate these for external-carrier shipments. See
            // uniuni.service.ts createShipment for the full rationale. DB
            // columns kept nullable so removing is a no-op if that changes.
            {
              title: 'Ship From', key: 'shipFrom',
              render: (_: unknown, r: any) => r.ship_from_name ?? (r.ss_warehouse_id ? `#${r.ss_warehouse_id}` : '-'),
            },
            {
              title: 'Contact', key: 'contact',
              render: (_: unknown, r: any) => {
                const p = typeof r.phone === 'string' ? r.phone.trim() : '';
                const isPlaceholder = p === '' || p.toLowerCase() === 'null' || /^\+?1?0+$/.test(p);
                if (!isPlaceholder) return p;
                return r.customerEmail || '-';
              },
            },
            {
              title: 'Created By', key: 'createdBy',
              render: (_: unknown, r: any) =>
                r.created_by_name
                  ?? r.created_by_email
                  ?? (r.created_by ? `#${r.created_by}` : '-'),
            },
            {
              // Inline item summary — matches the Orders page pattern so ops can
              // verify brand/SKU/qty at a glance without opening each order.
              // Falls back to SKU when name is missing, then '?' as a last resort.
              title: 'Items',
              dataIndex: 'items',
              width: 240,
              render: (items: any) => {
                const list: any[] = Array.isArray(items)
                  ? items
                  : typeof items === 'string'
                    ? (() => { try { return JSON.parse(items); } catch { return []; } })()
                    : [];
                if (list.length === 0) return '-';
                return (
                  <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>
                    {list.map((i, idx) => (
                      <div key={idx}>{`${i.name ?? i.sku ?? '?'} x${i.quantity ?? 1}`}</div>
                    ))}
                  </div>
                );
              },
            },
            { title: 'Customer Name', dataIndex: 'customerName', render: (v: string) => v || '-' },
            { title: 'City', dataIndex: 'city', render: (v: string) => v || '-' },
            { title: 'State', dataIndex: 'state', render: (v: string) => v || '-' },
            { title: 'Postal Code', dataIndex: 'postalCode', render: (v: string) => v || '-' },
            { title: 'Country', dataIndex: 'country', render: (v: string) => v || '-' },
            {
              title: 'Created',
              dataIndex: 'created_at',
              render: (d: string) => d ? new Date(d).toLocaleDateString('en-US') : '-',
            },
            {
              title: 'Label Printed',
              dataIndex: 'label_printed_at',
              render: (d: string, r: any) =>
                d ? (
                  <Tooltip title={`${r.label_printed_count}× · ${r.label_last_printed_by_name ?? r.label_last_printed_by ?? ''}`}>
                    <Tag color="blue">
                      {new Date(d).toLocaleDateString('en-US')}
                      {r.label_printed_count > 1 ? ` ×${r.label_printed_count}` : ''}
                    </Tag>
                  </Tooltip>
                ) : (
                  <Tag>Not printed</Tag>
                ),
            },
            {
              title: 'Voided',
              dataIndex: 'voided_at',
              render: (d: string, r: any) =>
                d ? (
                  <Tooltip title={`${r.voided_by ?? ''}${r.void_reason ? ` · ${r.void_reason}` : ''}`}>
                    <Tag color="red">{new Date(d).toLocaleDateString('en-US')}</Tag>
                  </Tooltip>
                ) : '-',
            },
            {
              title: 'Action',
              render: (_: any, record: any) => (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Button size="small" type="primary" onClick={() => navigate(`/order/${record.orderId}`)}>
                    View
                  </Button>
                  {record.tracking_number && (
                    <Button size="small" onClick={() => openLabelPreview(record.tracking_number)}>
                      Label
                    </Button>
                  )}
                  <Button size="small" onClick={() => openSlipPreview(record.orderId, record.orderNumber)}>
                    Slip
                  </Button>
                  {record.tracking_number && (
                    <Button
                      size="small"
                      type="primary"
                      ghost
                      onClick={() => openLabelPlusSlipPreview(record.tracking_number, record.orderId, record.orderNumber)}
                    >
                      L+S
                    </Button>
                  )}
                  {record.tracking_number && (
                    <Button size="small" onClick={() => openTracking(record.tracking_number)}>
                      Track
                    </Button>
                  )}
                  <Button
                    size="small"
                    danger
                    disabled={Boolean(record.voided_at)}
                    onClick={() => openVoid(record)}
                  >
                    {record.voided_at ? 'Voided' : 'Void'}
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </div>

      <Modal
        title={
          <span>
            Tracking: <Typography.Text copyable code>{trackingModal.trackingNumber}</Typography.Text>
          </span>
        }
        open={trackingModal.open}
        onCancel={() => setTrackingModal(prev => ({ ...prev, open: false }))}
        footer={null}
        width={600}
      >
        {trackingModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : trackingModal.events.length === 0 ? (
          <p style={{ color: '#888' }}>No tracking events found.</p>
        ) : (
          <Timeline
            mode="left"
            items={trackingModal.events.map((ev: any) => ({
              color: trackingColor(ev.code),
              label: ev.pathTimeLocalized
                ? ev.pathTimeLocalized.slice(0, 16).replace('T', ' ')
                : ev.pathTimeGMT ?? '',
              children: (
                <div>
                  <div><strong>{ev.description_en || ev.pathInfo}</strong></div>
                  <div style={{ color: '#555', fontSize: 12 }}>{ev.pathAddress || ev.pathAddr}</div>
                  {ev.city && (
                    <div style={{ color: '#888', fontSize: 12 }}>
                      {[ev.city, ev.province, ev.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Modal>

      <Modal
        title={`Void Shipment${voidModal.orderNumber ? ` — ${voidModal.orderNumber}` : ''}`}
        open={voidModal.open}
        onCancel={() => setVoidModal((s) => ({ ...s, open: false }))}
        width={640}
        footer={
          voidModal.result
            ? [
                <Button
                  key="close"
                  type="primary"
                  onClick={() => setVoidModal((s) => ({ ...s, open: false }))}
                >
                  Close
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={() => setVoidModal((s) => ({ ...s, open: false }))} disabled={voidModal.submitting}>
                  Cancel
                </Button>,
                <Button key="void" danger type="primary" loading={voidModal.submitting} onClick={submitVoid}>
                  Void Shipment
                </Button>,
              ]
        }
      >
        {!voidModal.result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Typography.Paragraph>
              This will:
              <ul style={{ marginTop: 8 }}>
                <li>Cancel the label with UniUni ({voidModal.trackingNumber ?? 'no tracking'})</li>
                <li>Restore SS Inventory by re-crediting every successful decrement for this order</li>
                <li>
                  Set the order back to <strong>awaiting_shipment</strong> in our platform so it appears
                  in the pending queue again
                </li>
              </ul>
              ShipStation is <em>not</em> updated by this action — undo the "Mark as Shipped" in SS separately if needed.
            </Typography.Paragraph>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Reason (optional, for audit)</div>
              <Input.TextArea
                rows={2}
                placeholder="e.g. Voided in SS by ops"
                value={voidModal.reason}
                onChange={(e) => setVoidModal((s) => ({ ...s, reason: e.target.value }))}
                disabled={voidModal.submitting}
              />
            </div>
          </div>
        )}

        {voidModal.result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <strong>UniUni cancel:</strong>{' '}
              {voidModal.result.uniuniCancel.attempted ? (
                voidModal.result.uniuniCancel.ok ? (
                  <Tag color="green">success</Tag>
                ) : (
                  <>
                    <Tag color="orange">failed</Tag>{' '}
                    <Typography.Text type="secondary">{voidModal.result.uniuniCancel.error}</Typography.Text>
                  </>
                )
              ) : (
                <Tag>skipped — no tracking</Tag>
              )}
            </div>
            <div>
              <strong>Inventory restore:</strong>{' '}
              <Tag color={voidModal.result.inventoryRestore.failed === 0 ? 'green' : 'orange'}>
                {voidModal.result.inventoryRestore.restored}/{voidModal.result.inventoryRestore.attempted} restored
              </Tag>
              {voidModal.result.inventoryRestore.failed > 0 && (
                <span style={{ marginLeft: 8, color: '#c00' }}>
                  {voidModal.result.inventoryRestore.failed} failed — check Inventory Recon
                </span>
              )}
            </div>
            <div>
              <strong>Local state:</strong>{' '}
              {voidModal.result.localReverted ? (
                <Tag color="green">reverted to awaiting_shipment</Tag>
              ) : (
                <Tag color="red">not reverted</Tag>
              )}
            </div>
          </div>
        )}
      </Modal>

      <PdfPreviewModal
        open={previewModal.open}
        onClose={() => setPreviewModal((p) => ({ ...p, open: false }))}
        title={previewModal.title}
        filename={previewModal.filename}
        fetchBlob={previewModal.fetchBlob}
      />

      {/* Filter drawer — replaces the old right sidebar. */}
      <Drawer
        title="Filters"
        placement="right"
        width={360}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        extra={
          <Space>
            <Button onClick={clearFilters}>Clear</Button>
            <Button
              type="primary"
              onClick={() => {
                applyFilters();
                setFilterDrawerOpen(false);
              }}
            >
              Apply
            </Button>
          </Space>
        }
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>Order Number</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: 120 }} value={local.orderNumberOp} onChange={(v: string) => setLocal({ ...local, orderNumberOp: v })} options={OP_OPTIONS} />
            <Input placeholder="Order Number" value={local.orderNumber} onChange={e => setLocal({ ...local, orderNumber: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>UniUni Tracking</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: 120 }} value={local.trackingOp} onChange={(v: string) => setLocal({ ...local, trackingOp: v })} options={OP_OPTIONS} />
            <Input placeholder="Tracking #" value={local.tracking} onChange={e => setLocal({ ...local, tracking: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Customer Name</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: 120 }} value={local.nameOp} onChange={(v: string) => setLocal({ ...local, nameOp: v })} options={OP_OPTIONS} />
            <Input placeholder="Customer Name" value={local.name} onChange={e => setLocal({ ...local, name: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Customer Email</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: 120 }} value={local.emailOp} onChange={(v: string) => setLocal({ ...local, emailOp: v })} options={OP_OPTIONS} />
            <Input placeholder="Email" value={local.email} onChange={e => setLocal({ ...local, email: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>City</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: 120 }} value={local.cityOp} onChange={(v: string) => setLocal({ ...local, cityOp: v })} options={OP_OPTIONS} />
            <Input placeholder="City" value={local.city} onChange={e => setLocal({ ...local, city: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Postal Code</label>
          <Space.Compact style={{ width: '100%' }}>
            <Select style={{ width: 120 }} value={local.postalCodeOp} onChange={(v: string) => setLocal({ ...local, postalCodeOp: v })} options={OP_OPTIONS} />
            <Input placeholder="Postal Code" value={local.postalCode} onChange={e => setLocal({ ...local, postalCode: e.target.value })} />
          </Space.Compact>
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Brand / Store</label>
          <Select
            mode="multiple"
            allowClear
            placeholder="All brands"
            style={{ width: '100%' }}
            value={local.storeIds}
            onChange={(v: number[]) => setLocal({ ...local, storeIds: v ?? [] })}
            options={storeOptions}
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Ship From (Warehouse)</label>
          <Select
            mode="multiple"
            allowClear
            placeholder="All warehouses"
            style={{ width: '100%' }}
            value={local.warehouseIds}
            onChange={(v: string[]) => setLocal({ ...local, warehouseIds: v ?? [] })}
            options={shipFromOptions}
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Created By</label>
          <Select
            mode="multiple"
            allowClear
            placeholder="Anyone"
            style={{ width: '100%' }}
            value={local.createdBy}
            onChange={(v: number[]) => setLocal({ ...local, createdBy: v ?? [] })}
            options={userOptions}
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Batch ID</label>
          <Input
            placeholder="UUID from a Ship Selected batch"
            value={local.batchId}
            onChange={(e) => setLocal({ ...local, batchId: e.target.value })}
            allowClear
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Status</label>
          <Select
            allowClear
            placeholder="All statuses"
            style={{ width: '100%' }}
            value={local.status}
            onChange={(v: string | undefined) => setLocal({ ...local, status: v })}
            options={[
              { value: 'label_created',      label: 'Label Created' },
              { value: 'in_transit',         label: 'In Transit' },
              { value: 'out_for_delivery',   label: 'Out for Delivery' },
              { value: 'delivery_attempted', label: 'Delivery Attempted' },
              { value: 'rescheduled',        label: 'Rescheduled' },
              { value: 'address_issue',      label: 'Address Issue' },
              { value: 'rts',                label: 'RTS' },
              { value: 'lost',               label: 'Lost' },
              { value: 'voided',             label: 'Voided' },
              { value: 'delivered',          label: 'Delivered' },
            ]}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Created Date</label>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            value={local.from && local.to ? [dayjs(local.from), dayjs(local.to)] : undefined}
            onChange={dates =>
              setLocal({
                ...local,
                from: dates?.[0]?.format('YYYY-MM-DD'),
                to: dates?.[1]?.format('YYYY-MM-DD'),
              })
            }
          />
        </Space>

      </Drawer>
    </div>
  );
}
